import { api } from "../scripts/api.js";

/*
   <app-class-readiness> - Team readiness for one class (2026-10-19).
   Route: #class-readiness/<classId>             the GRID: players down, skill areas across, a coloured square each
          #class-readiness/<classId>/grid/<area> one area opened: players down, that area's skills across (2026-10-20)
          #class-readiness/<classId>/players     players: position, how many required skills are met, top gaps
          #class-readiness/<classId>/team        skills: how the students each one is required for stand
          #class-readiness/<classId>/coach       COACHING (2026-10-20): the week's focus, suggested packs with due dates
                                                 (who has done them), and requests to David for more practice

   For David Z.: set each player's position, see who is below target on what, and assign the pack
   the page suggests. What counts as "required" comes from the Skill map (David): every Foundational
   skill, plus the player's position's skills rated 2 or 3, at that position's target tier.
   Evidence is the XP rule: a clean clear of a Game Launcher tab carrying the skill, at its tier.
   Server: GET /classes/:id/readiness, PUT /classes/:id/readiness/position (routes/skill-map.js).
   Coaching: GET /classes/:id/coaching, PUT /classes/:id/focus, POST and DELETE /classes/:id/coach-assignments,
   POST /classes/:id/skill-requests (lib/coaching.js). Teachers and TAs of the class. The players see the focus
   and the suggestions in the Game Launcher, above Next up.
*/

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const POS = ["Point", "Molder", "Closer"];
const ST = {
   met: ["Met", "rd-met"], below: ["Below", "rd-below"], started: ["Started", "rd-started"],
   untested: ["Untested", "rd-untested"], no_test: ["No test yet", "rd-notest"],
};
// Display order of the areas (the Skill map's order); an area not listed sorts after them.
const AREA_ORDER = ["Lookups", "Conditional math", "Logic", "Text", "Sort, rank and filter", "Array building",
                    "LET and LAMBDA", "References and position", "Dates and times", "Math and rounding", "Case craft"];
const areaRank = (a) => (AREA_ORDER.indexOf(a) < 0 ? AREA_ORDER.length : AREA_ORDER.indexOf(a));
const KIND = { more_practice: "More practice", harder: "Harder tabs", new_skill: "New skill", other: "Other" };
const RQ = { open: ["Open", "rd-untested"], building: ["Being built", "rd-below"], done: ["Done", "rd-met"], declined: ["Declined", "rd-notest"] };
const CO_REASON = {
   pack_not_open_here: "that pack is not open in this class", not_a_player_here: "pick players of this class", invalid_due_on: "that date does not exist",
   pick_players: "pick who it is for", no_players: "nobody plays that position yet", too_many: "at most 8 focus skills",
   skill_not_on_map: "pick decided skills (not To review)", pick_a_skill_or_topic: "pick a skill or type a topic",
   coaching_not_installed: "the coaching migration has not run yet",
};
const RQ_FIELDS = ["co-rq-skill", "co-rq-topic", "co-rq-kind", "co-rq-note"];
const ASSIGN_FIELDS = ["co-to", "co-skill", "co-pack", "co-due", "co-note", "co-players"];
const coReason = (r, d) => `${CO_REASON[r] || r}${d && !CO_REASON[r] ? ` (${d})` : ""}`;
const inDays = (n) => { const d = new Date(Date.now() + n * 864e5); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
const shortDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "");
const chip = (r) => `<span class="rd-chip ${ST[r.status][1]}" title="${esc(r.name)}: ${ST[r.status][0]}${r.best ? `, best tier ${r.best}` : ""}, target tier ${r.target}">${esc(r.name)}${
   r.status === "below" ? ` T${r.best}/${r.target}` : r.status === "met" ? " ✓" : ` T${r.target}`}</span>`;

class AppClassReadiness extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="class-readiness">
            <div class="page-head">
               <button type="button" id="rd-back">&larr; Class</button>
               <h1>Team readiness <select id="rd-class" title="Class" hidden></select></h1>
               <p class="sub">Each player against the skills their position needs: every <b>Foundational</b> skill plus their position's own,
                  at the target tier set on the Skill map. A skill is <b>Met</b> once they clear a Game Launcher tab carrying it at that tier, cleanly.</p>
            </div>
            <div class="pr-tabs" role="group" aria-label="View">
               <a class="pr-tab" data-tab="grid">Grid</a>
               <a class="pr-tab" data-tab="students">Players</a>
               <a class="pr-tab" data-tab="team">Skills across the team</a>
               <a class="pr-tab" data-tab="coach">Coaching</a>
            </div>
            <p class="msg" id="rd-msg"></p>
            <div id="rd-body"></div>
         </section>`;
      this.querySelector("#rd-back").addEventListener("click", () => {
         location.hash = this.classId ? `#class-detail/${this.classId}` : "#classes";
      });
      this.posFilter = "";
      this.querySelector("#rd-class").addEventListener("change", (e) => {
         if (e.target.value) location.hash = `#class-readiness/${e.target.value}`;
      });
   }

   /* The classes this person teaches (menu entry: no class in the address). */
   async classes() {
      if (!this._myClasses) {
         try {
            const r = await api("/list-classes");
            const d = await r.json();
            this._myClasses = (Array.isArray(d) ? d : d.classes || []).filter((c) => c.active !== false)
               .map((c) => ({ id: Number(c.class_id), title: c.title || [c.course_code, c.section].filter(Boolean).join(" ") || `Class ${c.class_id}` }));
         } catch { this._myClasses = []; }
      }
      return this._myClasses;
   }

   async fillClassPicker(current) {
      const list = await this.classes();
      const sel = this.querySelector("#rd-class");
      if (list.length < 2) { sel.hidden = true; return; }
      sel.innerHTML = list.map((c) => `<option value="${c.id}" ${c.id === current ? "selected" : ""}>${esc(c.title)}</option>`).join("");
      sel.hidden = false;
   }

   /* Opened from the menu: go to the last class viewed, else the ConCats-like one, else ask. */
   async pickClass() {
      const body = this.querySelector("#rd-body");
      const list = await this.classes();
      let last = null;
      try { last = Number(localStorage.getItem("rd-last-class")) || null; } catch { /* storage blocked */ }
      const pick = list.find((c) => c.id === last) || (list.length === 1 ? list[0] : null)
         || list.find((c) => /concats|esports/i.test(c.title));
      if (pick) { location.replace(`#class-readiness/${pick.id}`); return; }
      body.innerHTML = list.length
         ? `<p class="rd-note">Pick a class:</p><ul class="rd-pick">${list.map((c) => `<li><a href="#class-readiness/${c.id}">${esc(c.title)}</a></li>`).join("")}</ul>`
         : `<p class="empty">You have no classes.</p>`;
   }

   msg(t, kind = "") { const m = this.querySelector("#rd-msg"); m.textContent = t; m.className = `msg${kind ? " " + kind : ""}`; }

   async show(param) {
      const [c, sub, ...restParts] = String(param || "").split("/");
      this.gridArea = sub === "grid" && restParts.length ? restParts.join("/") : null;
      const classId = Number(c);
      const body = this.querySelector("#rd-body");
      if (!c || !Number.isInteger(classId)) { await this.pickClass(); return; }
      try { localStorage.setItem("rd-last-class", String(classId)); } catch { /* storage blocked */ }
      this.fillClassPicker(classId);
      const sameClass = this.classId === classId && this.data && this.data.class_id === classId;
      this.classId = classId;
      this.tab = sub === "team" ? "team" : sub === "players" ? "students" : sub === "coach" ? "coach" : "grid";
      const SUB = { team: "/team", students: "/players", coach: "/coach", grid: "" };
      this.querySelectorAll(".pr-tab").forEach((a) => {
         a.href = `#class-readiness/${classId}${SUB[a.dataset.tab]}`;
         a.classList.toggle("on", a.dataset.tab === this.tab);
      });
      if (sameClass && !this.reload) { this.render(); return; }    // switching tabs: no refetch
      this.reload = false;
      await this.load();
   }

   async load() {
      const body = this.querySelector("#rd-body");
      const seq = (this.seq = (this.seq || 0) + 1);                  // only the newest response may draw
      if (!this.data || this.data.class_id !== this.classId) body.innerHTML = `<p class="empty">Loading (this syncs every player's clears first)...</p>`;
      this.msg("");
      try {
         const [res, coach] = await Promise.all([api(`/classes/${this.classId}/readiness`), this.fetchCoach()]);
         const d = await res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
         if (seq !== this.seq) return;
         if (!d.ok) throw new Error(d.reason || "failed");
         this.data = d;
         this.coach = coach;
         this.render();
      } catch (e) {
         if (seq !== this.seq) return;
         body.innerHTML = "";
         this.msg(e.message === "skill_map_not_installed" ? "The skill map is not installed on the server yet." : `Could not load (${e.message}).`, "err");
      }
   }

   /* The coaching panel's data; never fails the page (null + a reason when the migration has not run). */
   async fetchCoach() {
      try {
         const r = await api(`/classes/${this.classId}/coaching`);
         const d = await r.json().catch(() => ({ ok: false, reason: `http_${r.status}` }));
         if (!d.ok) { this.coachErr = d.reason; return null; }
         this.coachErr = null;
         return d;
      } catch { this.coachErr = "offline"; return null; }
   }

   /* Refetch after an action. keep: ids of form fields whose typed values survive the redraw. A reply for a
      class the page has since left is dropped. */
   async reloadCoach(keep = []) {
      const cls = this.classId;
      const saved = keep.filter((id) => id !== "co-players").map((id) => {
         const el = this.querySelector(`#${id}`);
         return el ? [id, el.type === "checkbox" ? el.checked : el.value] : null;
      }).filter(Boolean);
      const ticks = keep.includes("co-players") ? [...this.querySelectorAll("#co-players input:checked")].map((x) => x.value) : null;
      const c = await this.fetchCoach();
      if (cls !== this.classId) return;
      if (c) this.coach = c;
      this.render();
      if (this.tab !== "coach") return;
      for (const [id, v] of saved) { const el = this.querySelector(`#${id}`); if (el) el.value = v; }
      if (keep.includes("co-skill")) this.fillPacks(Number(saved.find((x) => x[0] === "co-pack")?.[1]) || null);
      if (ticks) {
         this.querySelector("#co-players").hidden = this.querySelector("#co-to").value !== "pick";
         this.querySelectorAll("#co-players input").forEach((x) => { x.checked = ticks.includes(x.value); });
      }
   }

   /* Open suggestions a player has not done yet. */
   openFor(uid) {
      if (!this.coach) return [];
      return this.coach.assignments.filter((a) => a.students.some((x) => x.user_id === uid && !x.done_at));
   }

   focusIds() { return new Set(this.coach ? this.coach.focus.map((f) => f.skill_id) : []); }

   focusStrip() {
      if (!this.coach) return "";
      const f = this.coach.focus;
      return `<p class="rd-focus">${f.length ? `<b>This week's focus:</b> ${f.map((x) => `<span class="rd-chip rd-focus-chip" title="${esc(x.note || x.area || "")}">★ ${esc(x.name)}</span>`).join(" ")}`
         : `<b>No focus set this week.</b>`} <a href="#class-readiness/${this.classId}/coach">${f.length ? "Change" : "Set one"}</a></p>`;
   }

   /* Go to the Coaching tab with the suggestion form filled in. */
   suggestFor(pre) {
      this.prefill = pre;
      location.hash = `#class-readiness/${this.classId}/coach`;
   }

   render() {
      if (this.tab === "coach") return this.renderCoach();
      if (this.tab === "team") return this.renderTeam();
      if (this.tab === "students") return this.renderStudents();
      return this.gridArea ? this.renderArea(this.gridArea) : this.renderGrid();
   }

   filtered() {
      return this.data.students.filter((s) => !this.posFilter || (this.posFilter === "none" ? !s.position : s.position === this.posFilter));
   }

   /* An area's squares: how many of the player's required skills in it are met (testable ones only). */
   areaCell(rows) {
      if (!rows.length) return { cls: "rd-g-none", main: "", sub: "", tip: "Nothing in this area is required for this player" };
      const n = (st) => rows.filter((r) => r.status === st).length;
      const testable = rows.length - n("no_test");
      if (!testable) return { cls: "rd-g-notest", main: "no tab", sub: `${rows.length} skill${rows.length === 1 ? "" : "s"}`, tip: "No tab can test these targets yet" };
      const met = n("met"), below = n("below"), started = n("started"), untested = n("untested");
      const pct = met / testable;
      const cls = pct >= 1 ? "rd-g-5" : pct >= 0.67 ? "rd-g-4" : pct >= 0.34 ? "rd-g-3" : met > 0 ? "rd-g-2"
         : below + started > 0 ? "rd-g-1" : "rd-g-0";
      const sub = below ? `${below} below` : started ? `${started} started` : untested ? `${untested} untested` : "all met";
      const tip = `${met} met, ${below} below target, ${started} started, ${untested} untested` + (n("no_test") ? `, ${n("no_test")} with no tab yet` : "");
      return { cls, main: `${met}/${testable}`, sub, tip };
   }

   areasOf(students) {
      const seen = new Set();
      for (const s of students) for (const r of s.rows) seen.add(r.area || "No area");
      return [...seen].sort((a, b) => areaRank(a) - areaRank(b) || a.localeCompare(b));
   }

   gridLegend() {
      return `<div class="rd-g-legend">
         <span><i class="rd-g-5"></i>all met</span><span><i class="rd-g-4"></i>two thirds or more</span><span><i class="rd-g-3"></i>a third or more</span>
         <span><i class="rd-g-2"></i>some met</span><span><i class="rd-g-1"></i>tried, none met</span><span><i class="rd-g-0"></i>no evidence yet</span>
         <span><i class="rd-g-notest"></i>no tab can test it</span><span><i class="rd-g-none"></i>not required</span></div>`;
   }

   renderGrid() {
      const body = this.querySelector("#rd-body");
      const list = this.filtered();
      const areas = this.areasOf(this.data.students);
      if (!areas.length) { body.innerHTML = this.head() + `<p class="empty">No required skills yet: decide some skills on the Skill map first.</p>`; this.wireHead(); return; }
      const cls = this.classId;
      body.innerHTML = this.head() + `
         ${this.focusStrip()}
         <p class="rd-note">Each square is one player in one skill area: how many of the skills their position needs there are met (of those a tab can test).
            Click an area, or any square in it, to see the area skill by skill.</p>
         ${this.gridLegend()}
         <div class="rd-g-scroll"><table class="rd-grid">
            <thead><tr><th class="rd-g-name">Player</th><th class="rd-g-all">All</th>
               ${areas.map((a) => `<th class="rd-g-area"><a href="#class-readiness/${cls}/grid/${encodeURIComponent(a)}" title="Open ${esc(a)} skill by skill">${esc(a)}</a></th>`).join("")}</tr></thead>
            <tbody>${list.map((s) => {
               const all = this.areaCell(s.rows);
               const todo = this.openFor(s.user_id);
               return `<tr><td class="rd-g-name"><b>${esc(s.name)}</b><div class="rd-sub">${esc(s.position || "no position yet")}${todo.length
                     ? ` · <span class="rd-todo" title="${esc(todo.map((a) => `${a.title}${a.due_on ? `, due ${shortDate(a.due_on)}` : ""}`).join("; "))}">${todo.length} suggested</span>` : ""}</div></td>
                  <td><div class="rd-g-sq ${all.cls}" title="${esc(all.tip)}"><b>${esc(all.main)}</b><span>${esc(all.sub)}</span></div></td>
                  ${areas.map((a) => {
                     const c = this.areaCell(s.rows.filter((r) => (r.area || "No area") === a));
                     return `<td><a class="rd-g-sq ${c.cls}" href="#class-readiness/${cls}/grid/${encodeURIComponent(a)}" title="${esc(`${s.name}, ${a}: ${c.tip}`)}">
                        <b>${esc(c.main)}</b><span>${esc(c.sub)}</span></a></td>`;
                  }).join("")}</tr>`;
            }).join("")}</tbody>
         </table></div>`;
      this.wireHead();
   }

   /* One area opened: players down, the area's skills across, a square per player and skill. */
   renderArea(area) {
      const body = this.querySelector("#rd-body");
      const list = this.filtered();
      const skills = new Map();
      for (const s of this.data.students) for (const r of s.rows) {
         if ((r.area || "No area") === area && !skills.has(r.skill_id)) skills.set(r.skill_id, { id: r.skill_id, name: r.name, scope: r.scope });
      }
      const cols = [...skills.values()].sort((a, b) => a.name.localeCompare(b.name));
      const cell = (s, sk) => {
         const r = s.rows.find((x) => x.skill_id === sk.id);
         if (!r) return `<div class="rd-g-sq rd-g-none" title="${esc(`${s.name}: not required for ${s.position || "a player with no position"}`)}"></div>`;
         const map = {
            met: ["rd-g-5", `✓ T${r.best}`, `target ${r.target}`],
            below: ["rd-g-2", `T${r.best}`, `needs ${r.target}`],
            started: ["rd-g-1", "tried", `target ${r.target}`],
            untested: ["rd-g-0", "–", `target ${r.target}`],
            no_test: ["rd-g-notest", "no tab", `target ${r.target}`],
         }[r.status];
         const tip = `${s.name}, ${r.name}: ${ST[r.status][0]}${r.best ? `, best clear tier ${r.best}` : ""}, target tier ${r.target}`
            + (r.suggest ? `. Next: ${r.suggest.title} (tier ${r.suggest.tier})` : "");
         if (r.suggest && r.status !== "met" && this.coach) {
            return `<a class="rd-g-sq ${map[0]}" href="#class-readiness/${this.classId}/coach" data-uid="${s.user_id}" data-skill="${r.skill_id}" data-pack="${r.suggest.pack_id}"
               title="${esc(tip + ". Click to suggest it to " + s.name)}"><b>${esc(map[1])}</b><span>${esc(map[2])}</span></a>`;
         }
         return `<div class="rd-g-sq ${map[0]}" title="${esc(tip)}"><b>${esc(map[1])}</b><span>${esc(map[2])}</span></div>`;
      };
      const fo = this.focusIds();
      body.innerHTML = this.head() + `
         <p class="rd-note"><a href="#class-readiness/${this.classId}">&larr; All areas</a> · <b>${esc(area)}</b>, skill by skill.
            Each square shows the best tier the player has cleared and the target for their position; hover for the pack to play next${this.coach ? ", click to suggest it" : ""}.
            ${fo.size ? "★ = this week's focus." : ""}</p>
         ${this.gridLegend()}
         ${cols.length ? `<div class="rd-g-scroll"><table class="rd-grid rd-grid-skills">
            <thead><tr><th class="rd-g-name">Player</th>${cols.map((k) => `<th class="rd-g-skill" title="${esc(k.name)} (${k.scope === "foundational" ? "Foundational" : "Position"})">
               <span>${fo.has(k.id) ? "★ " : ""}${esc(k.name)}</span>${k.scope === "foundational" ? `<i class="rd-g-f">F</i>` : ""}</th>`).join("")}</tr></thead>
            <tbody>${list.map((s) => `<tr><td class="rd-g-name"><b>${esc(s.name)}</b><div class="rd-sub">${esc(s.position || "no position yet")}</div></td>
               ${cols.map((k) => `<td>${cell(s, k)}</td>`).join("")}</tr>`).join("")}</tbody>
         </table></div>` : `<p class="empty">Nothing in ${esc(area)} is required yet.</p>`}`;
      this.wireHead();
      body.querySelectorAll("a.rd-g-sq[data-pack]").forEach((a) => a.addEventListener("click", (e) => {
         e.preventDefault();
         this.suggestFor({ user_ids: [Number(a.dataset.uid)], skill_id: Number(a.dataset.skill), pack_id: Number(a.dataset.pack) });
      }));
   }

   head() {
      const d = this.data;
      return `
         <div class="rd-bar">
            <label>Position <select id="rd-pos"><option value="">All</option>${[...POS, "none"].map((p) =>
               `<option value="${p}" ${this.posFilter === p ? "selected" : ""}>${p === "none" ? "No position yet" : p}</option>`).join("")}</select></label>
            ${this.tab === "grid" ? "" : `<span class="rd-legend">${Object.entries(ST).map(([k, [l, cls]]) => `<span class="rd-chip ${cls}">${l}</span>`).join("")}</span>`}
         </div>
         ${d.review_left ? `<p class="rd-note">${d.review_left} skills on the Skill map are still To review, so they are not checked yet.</p>` : ""}`;
   }

   wireHead() {
      this.querySelector("#rd-pos").addEventListener("change", (e) => { this.posFilter = e.target.value; this.render(); });
   }

   renderStudents() {
      const body = this.querySelector("#rd-body");
      const list = this.filtered();
      body.innerHTML = this.head() + (list.length ? `
         <div class="cx-scroll"><table class="cd-table rd-table">
            <thead><tr><th>Player</th><th>Position</th><th>Ready</th><th>Met</th><th>Below</th><th>Started</th><th>Untested</th><th>Work on next (suggested pack)</th></tr></thead>
            <tbody>${list.map((s) => this.studentRow(s)).join("")}</tbody>
         </table></div>
         <p class="rd-note">Click a player for every skill on their list. Without a position, a player is checked on Foundational skills only.</p>`
         : `<p class="empty">No players${this.posFilter ? " in this position" : ""}.</p>`);
      this.wireHead();
      body.querySelectorAll(".rd-possel").forEach((sel) => sel.addEventListener("change", () => this.setPosition(sel)));
      body.querySelectorAll(".rd-suggest").forEach((b) => b.addEventListener("click", (e) => {
         e.stopPropagation();
         this.suggestFor({ user_ids: [Number(b.dataset.uid)], skill_id: Number(b.dataset.skill), pack_id: Number(b.dataset.pack) });
      }));
      body.querySelectorAll("tr.rd-player").forEach((tr) => tr.addEventListener("click", (e) => {
         if (e.target.closest("select, a, button")) return;
         const det = body.querySelector(`tr.rd-detail[data-uid="${tr.dataset.uid}"]`);
         det.hidden = !det.hidden;
      }));
   }

   studentRow(s) {
      const m = s.summary;
      const gaps = s.rows.filter((r) => r.status !== "met" && r.status !== "no_test").slice(0, 3);
      const bar = m.pct == null ? `<span class="rd-sub">nothing testable yet</span>`
         : `<div class="rd-bar-out"><div class="rd-bar-in" style="width:${m.pct}%"></div></div><span class="rd-pct">${m.pct}%</span>`;
      return `
         <tr class="rd-player" data-uid="${s.user_id}">
            <td><b>${esc(s.name)}</b><div class="rd-sub">${esc(s.email)}${s.squad ? ` · ${esc(s.squad)}` : ""}</div></td>
            <td><select class="rd-possel" data-uid="${s.user_id}"><option value="">-</option>${POS.map((p) => `<option ${s.position === p ? "selected" : ""}>${p}</option>`).join("")}</select>
               <span class="msg rd-posmsg"></span></td>
            <td class="rd-ready">${bar}<div class="rd-sub">${m.met} of ${m.required - m.no_test} testable</div></td>
            <td class="cx-num">${m.met}</td><td class="cx-num">${m.below}</td><td class="cx-num">${m.started}</td><td class="cx-num">${m.untested}</td>
            <td class="rd-next">${this.coachLines(s)}${gaps.length ? gaps.map((r) => `<div>${chip(r)}${r.suggest ? ` <span class="rd-sub">→ ${esc(r.suggest.title)} (T${r.suggest.tier})</span>${this.coach
               ? ` <button type="button" class="rd-suggest" data-uid="${s.user_id}" data-skill="${r.skill_id}" data-pack="${r.suggest.pack_id}" title="Suggest this pack to ${esc(s.name)}">Suggest</button>` : ""}`
               : ` <span class="rd-sub">→ no pack in this class at ${(r.best || 0) + 1 >= r.target ? `tier ${r.target}` : `tiers ${(r.best || 0) + 1} to ${r.target}`}</span>`}</div>`).join("") : `<span class="rd-sub">${m.required ? "all testable skills met" : "no required skills yet"}</span>`}</td>
         </tr>
         <tr class="rd-detail" data-uid="${s.user_id}" hidden><td colspan="8">
            ${["foundational", "position"].map((sc) => {
               const rows = s.rows.filter((r) => r.scope === sc);
               return rows.length ? `<div class="rd-group"><b>${sc === "foundational" ? "Foundational" : `${esc(s.position || "")} skills`}</b> ${rows.map(chip).join(" ")}</div>` : "";
            }).join("") || `<span class="rd-sub">No required skills on the map for this player yet.</span>`}
         </td></tr>`;
   }

   /* What the coach has suggested to this player (done ones too). */
   coachLines(s) {
      if (!this.coach) return "";
      const mine = this.coach.assignments.map((a) => ({ a, me: a.students.find((x) => x.user_id === s.user_id) })).filter((x) => x.me);
      return mine.map(({ a, me }) => `<div class="rd-coachline ${me.done_at ? "done" : ""}">${me.done_at ? "✓" : "◦"} Coach: <b>${esc(a.title)}</b>${a.due_on ? ` <span class="rd-sub ${!me.done_at && a.due_on < inDays(0) ? "co-late" : ""}">due ${shortDate(a.due_on)}</span>` : ""}${
         me.done_at && me.best_pct != null ? ` <span class="rd-sub">${me.best_pct}%</span>` : ""}</div>`).join("");
   }

   async setPosition(sel) {
      const out = sel.parentElement.querySelector(".rd-posmsg");
      out.textContent = "Saving...";
      try {
         const res = await api(`/classes/${this.classId}/readiness/position`, { method: "PUT",
            body: JSON.stringify({ user_id: Number(sel.dataset.uid), position: sel.value || null }) });
         const d = await res.json();
         if (!d.ok) { out.textContent = `Not saved (${d.reason}).`; out.className = "msg rd-posmsg err"; return; }
         out.textContent = "Saved; updating...";
         await this.load();                                  // the player's required list changes with the position
      } catch { out.textContent = "Not saved (offline)."; out.className = "msg rd-posmsg err"; }
   }

   renderTeam() {
      const body = this.querySelector("#rd-body");
      const want = this.posFilter;
      const ids = new Set(this.data.students.filter((s) => !want || (want === "none" ? !s.position : s.position === want)).map((s) => s.user_id));
      // recount per skill for the filtered players
      const per = new Map();
      for (const s of this.data.students) {
         if (!ids.has(s.user_id)) continue;
         for (const r of s.rows) {
            if (!per.has(r.skill_id)) per.set(r.skill_id, { id: r.skill_id, name: r.name, area: r.area, scope: r.scope, met: 0, below: 0, started: 0, untested: 0, no_test: 0, n: 0 });
            const x = per.get(r.skill_id);
            x[r.status]++; x.n++;
         }
      }
      const rows = [...per.values()].sort((a, b) => (a.no_test === a.n ? 1 : 0) - (b.no_test === b.n ? 1 : 0) || a.met / a.n - b.met / b.n || b.n - a.n);
      body.innerHTML = this.head() + (rows.length ? `
         <p class="rd-note">Weakest first: skills where the fewest of the players who need them have met the target. These are the team's gaps.</p>
         <div class="cx-scroll"><table class="cd-table rd-table">
            <thead><tr><th>Skill</th><th>Area</th><th>Scope</th><th>Met</th><th>Below</th><th>Started</th><th>Untested</th><th></th>${this.coach ? "<th></th>" : ""}</tr></thead>
            <tbody>${rows.map((x) => `<tr>
               <td><b>${esc(x.name)}</b></td><td>${esc(x.area || "-")}</td><td>${x.scope === "foundational" ? "Foundational" : "Position"}</td>
               <td class="cx-num">${x.met} / ${x.n}</td><td class="cx-num">${x.below || ""}</td>
               <td class="cx-num">${x.started || ""}</td><td class="cx-num">${x.untested || ""}</td>
               <td>${x.no_test ? `<span class="rd-chip rd-notest" title="No tab can prove the target tier for ${x.no_test} of them">No test yet (${x.no_test})</span>` : ""}</td>
               ${this.coach ? `<td class="rd-acts"><button type="button" class="rd-ask" data-skill="${x.id}" title="Tell Coach Brown the team needs more practice on this">Ask for more</button>
                  <button type="button" class="rd-sugg-all" data-skill="${x.id}" title="Suggest a pack for this skill">Suggest</button></td>` : ""}</tr>`).join("")}</tbody>
         </table></div>` : `<p class="empty">No required skills yet: decide some skills on the Skill map first.</p>`);
      this.wireHead();
      body.querySelectorAll(".rd-ask").forEach((b) => b.addEventListener("click", () => this.suggestFor({ request_skill: Number(b.dataset.skill) })));
      body.querySelectorAll(".rd-sugg-all").forEach((b) => b.addEventListener("click", () => this.suggestFor({ skill_id: Number(b.dataset.skill) })));
   }

   /* ---------- Coaching (2026-10-20) ---------- */
   skillOptions(selected, { blank = "" } = {}) {
      const by = new Map();
      for (const k of this.coach.skills) {
         const a = k.area || "No area";
         if (!by.has(a)) by.set(a, []);
         by.get(a).push(k);
      }
      const areas = [...by.keys()].sort((a, b) => areaRank(a) - areaRank(b) || a.localeCompare(b));
      return `${blank ? `<option value="">${esc(blank)}</option>` : ""}${areas.map((a) => `<optgroup label="${esc(a)}">${by.get(a).sort((x, y) => x.name.localeCompare(y.name))
         .map((k) => `<option value="${k.skill_id}" ${k.skill_id === selected ? "selected" : ""}>${esc(k.name)}${k.priority === "now" ? " (priority)" : ""}</option>`).join("")}</optgroup>`).join("")}`;
   }

   renderCoach() {
      const body = this.querySelector("#rd-body");
      if (!this.coach) {
         body.innerHTML = `<p class="msg err">${this.coachErr === "coaching_not_installed" ? "Coaching is not installed on the server yet (migration 2026-10-20_coaching.sql)."
            : `Could not load coaching (${esc(this.coachErr || "unknown")}).`}</p>`;
         return;
      }
      const c = this.coach;
      const pre = this.prefill || {};
      this.prefill = null;
      // unsaved focus edits survive other actions (Suggest, Take down, Send); a fresh copy only for a new class or after Save
      if (!this.focusDraft || this.focusDraftFor !== this.classId || this.focusFresh) {
         this.focusDraft = c.focus.map((f) => ({ skill_id: f.skill_id, name: f.name, area: f.area, note: f.note || "" }));
         this.focusDraftFor = this.classId;
         this.focusFresh = false;
      }
      const players = c.players;
      const forPicked = pre.user_ids && pre.user_ids.length;
      body.innerHTML = `
         <p class="rd-note">What you set here shows up in each player's Game Launcher, above Next up: <b>From your coach</b> (your suggestions, with the due date)
            and <b>Work on your gaps</b> (packs for their weakest skills, this week's focus first, then Coach Brown's priorities). Nothing here is graded;
            a clean clear still earns the XP and counts toward readiness.</p>
         <div class="co-grid">
            <section class="co-card">
               <h2>This week's focus</h2>
               <p class="rd-note">Up to ${c.max_focus} skills, in order. Every player who needs one of them gets it first in their gap list.</p>
               <ol class="co-focus" id="co-focus"></ol>
               <div class="co-row"><select id="co-focus-add">${this.skillOptions(null, { blank: "Add a skill..." })}</select>
                  <button type="button" id="co-focus-addbtn">Add</button></div>
               <div class="co-row"><button type="button" id="co-focus-save" class="co-primary">Save focus</button><span class="msg" id="co-focus-msg"></span></div>
               ${c.focus.length && c.focus[0].set_on ? `<p class="rd-sub">Last set ${esc(shortDate(c.focus[0].set_on))}${c.focus[0].set_by_name ? ` by ${esc(c.focus[0].set_by_name)}` : ""}.</p>` : ""}
            </section>

            <section class="co-card co-wide">
               <h2>Suggest practice</h2>
               <div class="co-form">
                  <label>For <select id="co-to">
                     <option value="everyone">Everyone</option>${POS.map((p) => `<option value="${p}">All ${p}s</option>`).join("")}
                     <option value="pick" ${forPicked ? "selected" : ""}>Pick players...</option></select></label>
                  <div class="co-players" id="co-players" ${forPicked ? "" : "hidden"}>${players.map((p) => `<label class="co-check"><input type="checkbox" value="${p.user_id}"
                     ${forPicked && pre.user_ids.includes(p.user_id) ? "checked" : ""}> ${esc(p.name)}${p.position ? ` <span class="rd-sub">${esc(p.position)}</span>` : ""}</label>`).join("")}</div>
                  <label>Skill <select id="co-skill">${this.skillOptions(pre.skill_id || null, { blank: "(any skill)" })}</select></label>
                  <label>Pack <select id="co-pack"></select></label>
                  <label>Due <input type="date" id="co-due" value="${inDays(7)}"></label>
                  <label class="co-grow">Note <input type="text" id="co-note" maxlength="300" placeholder="optional: what to watch for"></label>
                  <div class="co-row"><button type="button" id="co-assign" class="co-primary">Suggest</button><span class="msg" id="co-assign-msg"></span></div>
               </div>
               <h3>Open suggestions</h3>
               ${c.assignments.length ? `<div class="cx-scroll"><table class="cd-table co-table">
                  <thead><tr><th>Pack</th><th>Skill</th><th>Due</th><th>Done</th><th>Players</th><th></th></tr></thead>
                  <tbody>${c.assignments.map((a) => `<tr>
                     <td><b>${esc(a.title)}</b>${a.note ? `<div class="rd-sub">${esc(a.note)}</div>` : ""}<div class="rd-sub">from ${esc(a.created_by_name || "?")}</div></td>
                     <td>${esc(a.skill_name || "-")}</td>
                     <td>${a.due_on ? `<span class="${a.due_on < inDays(0) && a.done < a.total ? "co-late" : ""}">${esc(shortDate(a.due_on))}</span>` : "-"}</td>
                     <td class="cx-num"><b>${a.done}</b> / ${a.total}</td>
                     <td>${a.students.map((x) => `<span class="rd-chip ${x.done_at ? "rd-met" : "rd-untested"}" title="${x.done_at ? `done${x.best_pct != null ? `, ${x.best_pct}%` : ""}` : "not yet"}">${x.done_at ? "✓ " : ""}${esc(x.name)}</span>`).join(" ")}</td>
                     <td><button type="button" class="co-down" data-id="${a.coach_assignment_id}" title="Take it off the players' launchers">Take down</button></td></tr>`).join("")}</tbody>
               </table></div>` : `<p class="empty">No suggestions yet.</p>`}
               <p class="rd-note">Done = the player finished a run of the pack after you suggested it. Tip: on the Grid, open an area and click a square to suggest that player's next pack.</p>
            </section>

            <section class="co-card">
               <h2>Ask Coach Brown for more practice</h2>
               <p class="rd-note">Tell Coach Brown what the team needs: more tabs for a skill, harder ones, or a skill that is not on the map yet. He answers on the Skill map.</p>
               <div class="co-form co-stack">
                  <label>Skill <select id="co-rq-skill">${this.skillOptions(pre.request_skill || null, { blank: "(not on the map: type a topic)" })}</select></label>
                  <label>Topic <input type="text" id="co-rq-topic" maxlength="120" placeholder="only if the skill is not on the map"></label>
                  <label>What <select id="co-rq-kind">${Object.entries(KIND).map(([k, l]) => `<option value="${k}">${l}</option>`).join("")}</select></label>
                  <label>Note <textarea id="co-rq-note" rows="3" maxlength="1000" placeholder="what you saw: who struggled, on which case or tab"></textarea></label>
                  <div class="co-row"><button type="button" id="co-rq-send" class="co-primary">Send to Coach Brown</button><span class="msg" id="co-rq-msg"></span></div>
               </div>
               <h3>Requests from this class</h3>
               ${c.requests.length ? `<ul class="co-reqs">${c.requests.map((r) => `<li><span class="rd-chip ${RQ[r.status][1]}">${RQ[r.status][0]}</span>
                  <b>${esc(r.skill_name || r.topic)}</b> <span class="rd-sub">${esc(KIND[r.kind] || r.kind)} · ${esc(r.created_by_name || "")} · ${esc(new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }))}</span>
                  ${r.note ? `<div class="rd-sub">${esc(r.note)}</div>` : ""}${r.reply ? `<div class="co-reply">${esc(r.resolved_by_name || "Coach Brown")}: ${esc(r.reply)}</div>` : ""}</li>`).join("")}</ul>`
                  : `<p class="empty">None yet.</p>`}
            </section>
         </div>`;
      this.drawFocus();
      this.fillPacks(pre.pack_id || null);
      this.wireCoach();
      if (pre.request_skill) body.querySelector("#co-rq-note").focus();
      else if (pre.pack_id || pre.skill_id) body.querySelector("#co-assign").scrollIntoView({ block: "center" });
   }

   drawFocus() {
      const ol = this.querySelector("#co-focus");
      const f = this.focusDraft;
      ol.innerHTML = f.length ? f.map((x, i) => `<li data-i="${i}"><span class="co-fname">★ ${esc(x.name)} <span class="rd-sub">${esc(x.area || "")}</span></span>
         <input type="text" class="co-fnote" maxlength="200" value="${esc(x.note)}" placeholder="note for the team (optional)">
         <button type="button" class="co-up" ${i ? "" : "disabled"} title="Move up">↑</button><button type="button" class="co-dn" ${i < f.length - 1 ? "" : "disabled"} title="Move down">↓</button>
         <button type="button" class="co-x" title="Remove">×</button></li>`).join("") : `<li class="rd-sub co-none">No focus yet: add a skill below.</li>`;
      ol.querySelectorAll("li[data-i]").forEach((li) => {
         const i = Number(li.dataset.i);
         li.querySelector(".co-fnote").addEventListener("input", (e) => { f[i].note = e.target.value; });
         li.querySelector(".co-up").addEventListener("click", () => { [f[i - 1], f[i]] = [f[i], f[i - 1]]; this.drawFocus(); });
         li.querySelector(".co-dn").addEventListener("click", () => { [f[i + 1], f[i]] = [f[i], f[i + 1]]; this.drawFocus(); });
         li.querySelector(".co-x").addEventListener("click", () => { f.splice(i, 1); this.drawFocus(); });
      });
   }

   /* Packs open in this class; with a skill picked, only the packs that show it. */
   fillPacks(selected) {
      const sel = this.querySelector("#co-pack");
      const skill = Number(this.querySelector("#co-skill").value) || null;
      const list = this.coach.packs.filter((p) => !skill || p.skill_ids.includes(skill));
      const tiers = (p) => (p.tiers.length ? ` (T${p.tiers[0]}${p.tiers.length > 1 ? `-${p.tiers[p.tiers.length - 1]}` : ""})` : "");
      // with a skill picked: how many of the pack's tabs carry it, and at which tiers (the pack's own range is not the skill's)
      const forSkill = (p) => {
         const t = (p.skill_tiers || {})[skill] || [];
         if (!t.length) return tiers(p);
         const lab = t.map((x) => (x == null ? "no tier" : `T${x}`));
         return `: ${t.length} of ${p.tabs} tab${p.tabs === 1 ? "" : "s"} with this skill (${lab.join(", ")})`;
      };
      sel.innerHTML = list.length ? list.map((p) => `<option value="${p.pack_id}" ${p.pack_id === selected ? "selected" : ""}>${esc(p.title)}${skill ? forSkill(p) : tiers(p)}</option>`).join("")
         : `<option value="">No open pack shows this skill</option>`;
      const out = this.querySelector("#co-assign-msg");
      if (!list.length && skill) {
         out.innerHTML = `No pack open in this class carries it. <a href="#" id="co-ask-instead">Ask Coach Brown for practice</a>`;
         out.className = "msg warn";
         out.querySelector("#co-ask-instead").addEventListener("click", (e) => {
            e.preventDefault();
            this.querySelector("#co-rq-skill").value = String(skill);
            this.querySelector("#co-rq-note").focus();
         });
      } else if (out.querySelector && out.querySelector("#co-ask-instead")) { out.textContent = ""; out.className = "msg"; }
   }

   async post(path, method, body) {
      const res = await api(path, { method, body: body ? JSON.stringify(body) : undefined });
      return res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
   }

   wireCoach() {
      const q = (id) => this.querySelector(id);
      q("#co-focus-addbtn").addEventListener("click", () => {
         const id = Number(q("#co-focus-add").value);
         if (!id || this.focusDraft.some((x) => x.skill_id === id)) return;
         if (this.focusDraft.length >= this.coach.max_focus) { q("#co-focus-msg").textContent = `At most ${this.coach.max_focus}.`; return; }
         const k = this.coach.skills.find((x) => x.skill_id === id);
         this.focusDraft.push({ skill_id: id, name: k.name, area: k.area, note: "" });
         q("#co-focus-add").value = "";
         this.drawFocus();
      });
      q("#co-focus-save").addEventListener("click", async () => {
         const out = q("#co-focus-msg");
         out.textContent = "Saving...";
         try {
            const d = await this.post(`/classes/${this.classId}/focus`, "PUT", { skills: this.focusDraft.map((x) => ({ skill_id: x.skill_id, note: x.note || null })) });
            if (!d.ok) { out.textContent = `Not saved: ${coReason(d.reason, d.detail)}.`; out.className = "msg err"; return; }
            this.focusFresh = true;
            await this.reloadCoach(RQ_FIELDS.concat(ASSIGN_FIELDS));
            const again = this.querySelector("#co-focus-msg");
            if (again) { again.textContent = "Saved. Players see it next time their launcher loads."; again.className = "msg ok"; }
         } catch { out.textContent = "Not saved (offline)."; out.className = "msg err"; }
      });
      q("#co-to").addEventListener("change", (e) => { q("#co-players").hidden = e.target.value !== "pick"; });
      q("#co-skill").addEventListener("change", () => this.fillPacks(Number(q("#co-pack").value) || null));
      q("#co-assign").addEventListener("click", async () => {
         const out = q("#co-assign-msg");
         const to = q("#co-to").value;
         const pack = Number(q("#co-pack").value);
         if (!pack) { out.textContent = "Pick a pack."; out.className = "msg err"; return; }
         const body = { pack_id: pack, skill_id: Number(q("#co-skill").value) || null, due_on: q("#co-due").value || null, note: q("#co-note").value || null };
         if (to === "everyone") body.everyone = true;
         else if (to === "pick") {
            body.user_ids = [...this.querySelectorAll("#co-players input:checked")].map((x) => Number(x.value));
            if (!body.user_ids.length) { out.textContent = "Tick at least one player."; out.className = "msg err"; return; }
         } else body.position = to;
         out.textContent = "Saving..."; out.className = "msg";
         try {
            const d = await this.post(`/classes/${this.classId}/coach-assignments`, "POST", body);
            if (!d.ok) { out.textContent = `Not saved: ${coReason(d.reason, d.detail)}.`; out.className = "msg err"; return; }
            await this.reloadCoach(RQ_FIELDS);
            const again = this.querySelector("#co-assign-msg");
            if (again) { again.textContent = `Suggested ${d.assignment.title} to ${d.assignment.total} player${d.assignment.total === 1 ? "" : "s"}.`; again.className = "msg ok"; }
         } catch { out.textContent = "Not saved (offline)."; out.className = "msg err"; }
      });
      this.querySelectorAll(".co-down").forEach((b) => b.addEventListener("click", async () => {
         b.disabled = true;
         try {
            const d = await this.post(`/classes/${this.classId}/coach-assignments/${b.dataset.id}`, "DELETE");
            if (!d.ok) { b.disabled = false; b.textContent = `Failed (${d.reason})`; return; }
            await this.reloadCoach(RQ_FIELDS.concat(ASSIGN_FIELDS));
         } catch { b.disabled = false; b.textContent = "Offline"; }
      }));
      q("#co-rq-send").addEventListener("click", async () => {
         const out = q("#co-rq-msg");
         const body = { skill_id: Number(q("#co-rq-skill").value) || null, topic: q("#co-rq-topic").value.trim() || null,
                        kind: q("#co-rq-kind").value, note: q("#co-rq-note").value.trim() || null };
         if (!body.skill_id && !body.topic) { out.textContent = "Pick a skill or type a topic."; out.className = "msg err"; return; }
         out.textContent = "Sending..."; out.className = "msg";
         try {
            const d = await this.post(`/classes/${this.classId}/skill-requests`, "POST", body);
            if (!d.ok) { out.textContent = `Not sent: ${coReason(d.reason, d.detail)}.`; out.className = "msg err"; return; }
            await this.reloadCoach(ASSIGN_FIELDS);
            const again = this.querySelector("#co-rq-msg");
            if (again) { again.textContent = "Sent. Coach Brown sees it on the Skill map, under Requests."; again.className = "msg ok"; }
         } catch { out.textContent = "Not sent (offline)."; out.className = "msg err"; }
      });
   }
}

customElements.define("app-class-readiness", AppClassReadiness);
